import { useCallback, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { log } from 'log';
import { prop } from 'ramda';
import socketIo from 'socket.io-client';
import { useAppSelector, useSocketSelector } from 'js/store/hooks';

import * as hooks from './hooks';
import * as actions from './store/actions';
import { SEND_SYMBOL } from '../../constants';
import { useGenericSend } from '../useGenericSend';

export const useOAPISocket = () => {
    const dispatch = useDispatch();
    const options = hooks.useOptions();
    const genericSend = useGenericSend();
    const onMessage = hooks.useOnMessage();
    const connection = useSocketSelector(prop('connection'));
    const areInitialResourcesLoaded = useAppSelector(prop('areInitialResourcesLoaded'));

    hooks.useOnForceReconnect();

    const onConnect = useCallback(() => {
        log('oapiInfo', 'Open API connected');

        dispatch(actions.setConnectedAction(true));
        dispatch(actions.toggleConnectionErrorAction(false));
    }, [dispatch]);

    const onError = useCallback(() => {
        log('oapiInfo', 'Open API connection error');

        dispatch(actions.setConnectedAction(false));
        dispatch(actions.toggleConnectionErrorAction(true));
    }, [dispatch]);

    const onConnecting = useCallback((transportType: string) => {
        log('oapiInfo', `Open API connecting via ${transportType}.`);
    }, []);

    const onConnectFailed = useCallback(() => {
        log('oapiInfo', 'Open API connection failed');
    }, []);

    const onDisconnect = useCallback(() => {
        log('oapiInfo', 'Open API disconnected');
        dispatch(actions.setConnectedAction(false));

        if (!connection?.socket.connecting) {
            connection?.socket.connect();
        }
    }, [connection, dispatch]);

    const onReconnectFailed = useCallback(() => {
        log('oapiInfo', 'Open API reconnect failed');
    }, []);

    const onReconnecting = useCallback((_: string, attemptNumber: number) => {
        log('oapiInfo', `Open API reconnecting: ${attemptNumber}`);
    }, []);

    useEffect(() => {
        window[SEND_SYMBOL] = genericSend;
    }, [genericSend]);

    // connect if connection is null
    useEffect(() => {
        if (connection) {
            return;
        }

        if (!options.url && areInitialResourcesLoaded) {
            onError();

            return;
        }

        if (!options.url) {
            return;
        }

        log('oapiInfo', `Trying to connect to Open API: ${options.url}`);
        dispatch(actions.setConnectionAction(socketIo.connect(options.url, options)));
    }, [areInitialResourcesLoaded, connection, dispatch, onError, options]);

    // disconnect if connection becomes null
    useEffect(() => {
        if (!connection || connection.socket.connecting) {
            return;
        }

        return () => {
            connection.disconnect();
        };
    }, [connection]);

    // subscribe if connection is not null
    useEffect(() => {
        if (!connection) {
            return;
        }

        connection.on('connect', onConnect);
        connection.on('connecting', onConnecting);
        connection.on('connect_failed', onConnectFailed);
        connection.on('reconnect', onConnect);
        connection.on('reconnect_failed', onReconnectFailed);
        connection.on('reconnecting', onReconnecting);
        connection.on('disconnect', onDisconnect);
        connection.on('message', onMessage);
        connection.on('error', onError);

        return () => {
            connection.removeAllListeners();
        };
    }, [
        onError,
        onMessage,
        onConnect,
        connection,
        onConnecting,
        onDisconnect,
        onReconnecting,
        onConnectFailed,
        onReconnectFailed
    ]);
};
